import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  MapPin, Navigation, Route, Clock, FileText, Phone, UtensilsCrossed, Trophy, ShieldCheck, TreePine,
  Download, ExternalLink, ChevronRight, ArrowUp, Map, Bike, Timer, AlertTriangle, CheckSquare,
  Thermometer, Stethoscope, Flag, Star, Utensils, FileDown, PhoneCall, CloudRain, Sun, Cloud, CloudSnow, CloudDrizzle, Wind, Droplets,
  Mountain, ArrowUpDown, Ruler, Upload, Trash2, FolderOpen
} from 'lucide-react';
import ShareButton from '../components/ShareButton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import GuideEndurance from '../components/GuideEndurance';
import ProgrammeEndurance from '../components/ProgrammeEndurance';
import ElevationProfile from '../components/ElevationProfile';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';

function GpxMapViewer({ gpxUrl, color = '#d946ef' }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!gpxUrl || !mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, { scrollWheelZoom: false }).setView([46.48, -0.04], 13);
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap'
    }).addTo(map);

    fetch(gpxUrl)
      .then(r => r.text())
      .then(gpxText => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(gpxText, 'text/xml');
        const pts = doc.querySelectorAll('trkpt');
        const coords = Array.from(pts).map(p => [
          parseFloat(p.getAttribute('lat')),
          parseFloat(p.getAttribute('lon'))
        ]);
        if (coords.length > 0) {
          const polyline = L.polyline(coords, { color: color, weight: 4, opacity: 0.9 }).addTo(map);
          map.fitBounds(polyline.getBounds(), { padding: [30, 30] });
          L.marker(coords[0], {
            icon: L.divIcon({ className: 'gpx-marker-start', html: '<div style="background:#16a34a;color:white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)">D</div>', iconSize: [28, 28], iconAnchor: [14, 14] })
          }).addTo(map).bindPopup('Départ');
          L.marker(coords[coords.length - 1], {
            icon: L.divIcon({ className: 'gpx-marker-end', html: '<div style="background:#dc2626;color:white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)">A</div>', iconSize: [28, 28], iconAnchor: [14, 14] })
          }).addTo(map).bindPopup('Arrivée');
        }
      })
      .catch(console.error);

    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [gpxUrl, color]);

  return <div ref={mapRef} className="w-full h-full min-h-[350px] rounded-xl z-0" data-testid="gpx-map" />;
}


function WeatherWidgetEmbed() {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const existing = containerRef.current.querySelector('script');
    if (existing) return;
    const script = document.createElement('script');
    script.src = 'https://weatherwidget.io/js/widget.min.js';
    script.async = true;
    script.id = 'weatherwidget-io-js';
    containerRef.current.appendChild(script);
  }, []);
  return (
    <div ref={containerRef} className="p-2" data-testid="weather-widget-embed">
      <a
        className="weatherwidget-io"
        href="https://forecast7.com/fr/46d490d07/sanxay/"
        data-label_1="SANXAY"
        data-label_2="Météo 14 jours"
        data-days="14"
        data-theme="pure"
        data-basecolor=""
        data-textcolor="#333333"
        data-highcolor="#d946ef"
        data-lowcolor="#94a3b8"
        data-suncolor="#eab308"
        data-raincolor="#3b82f6"
        style={{ display: 'block', textDecoration: 'none' }}
      >
        SANXAY — Météo 14 jours
      </a>
    </div>
  );
}

const API = `${window.location.origin}/api`;

const HorseIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 2s-3 1-5 2c-2-2-5-2-5-2l-1 3c-1 0-3 1-4 3L5 9l3 3-2 4 3 1 2-2c1 1 3 2 5 2v3h2v-3h1v3h2v-4c1-1 2-3 2-5V5l-1-3z" />
    <circle cx="17" cy="7" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const allSections = [
  { id: 'acces', labelKey: 'ip.navAcces', icon: MapPin },
  { id: 'parcours', labelKey: 'ip.navParcours', icon: Route },
  { id: 'programme', labelKey: 'ip.navProgramme', icon: Clock },
  { id: 'documents', labelKey: 'ip.navDocuments', icon: FileText },
  { id: 'contacts', labelKey: 'ip.navContacts', icon: Phone },
  { id: 'restauration', labelKey: 'ip.navRestau', icon: UtensilsCrossed },
  { id: 'resultats', labelKey: 'ip.navResultats', icon: Trophy },
  { id: 'securite', labelKey: 'ip.navSecurite', icon: ShieldCheck },
  { id: 'conseils', labelKey: 'ip.navConseils', label: 'Conseils', icon: TreePine },
];

export default function InfosPratiques() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('acces');
  const [eventData, setEventData] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const sectionRefs = useRef({});
  const navRef = useRef(null);
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getUser = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch { return null; }
  };
  const isStaff = ['admin', 'collaborateur'].includes(getUser()?.role);

  useEffect(() => {
    fetchEvents();
    fetchWeather();
  }, []);

  useEffect(() => {
    if (selectedEventId) fetchEventInfos(selectedEventId);
  }, [selectedEventId]);

  // Scroll spy
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0.1 }
    );
    Object.values(sectionRefs.current).forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, [eventData]);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
      if (res.data.length > 0) setSelectedEventId(res.data[0].id);
    } catch (e) { console.error(e); }
  };

  const fetchEventInfos = async (eventId) => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/infos-pratiques`);
      setEventData(res.data);
    } catch (e) {
      setEventData({
        event_id: eventId,
        acces: {}, parcours: [], programme: [], documents: [],
        contacts: [], restauration: {}, resultats: {},
        securite: [], conseils: []
      });
    }
  };

  const fetchWeather = async () => {
    // Try direct browser call first (most reliable - avoids Kubernetes network issues)
    const directUrl = 'https://api.open-meteo.com/v1/forecast?latitude=46.4875&longitude=-0.0395&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max&timezone=Europe/Paris&forecast_days=15';
    try {
      const res = await fetch(directUrl);
      if (res.ok) {
        const data = await res.json();
        if (data?.daily?.time) { setWeather(data); setWeatherLoading(false); return; }
      }
    } catch (e) { /* Direct call failed, try proxy */ }
    // Fallback: backend proxy
    try {
      const res = await axios.get(`${API}/weather`);
      if (res.data?.daily?.time) { setWeather(res.data); setWeatherLoading(false); return; }
    } catch (e) { /* Proxy also failed */ }
    setWeatherLoading(false);
  };

  const getWeatherIcon = (code) => {
    if (code <= 1) return { icon: Sun, color: 'text-yellow-500', bg: 'bg-yellow-50' };
    if (code <= 3) return { icon: Cloud, color: 'text-zinc-400', bg: 'bg-zinc-50' };
    if (code <= 48) return { icon: Cloud, color: 'text-zinc-500', bg: 'bg-zinc-100' };
    if (code <= 57) return { icon: CloudDrizzle, color: 'text-blue-400', bg: 'bg-blue-50' };
    if (code <= 67) return { icon: CloudRain, color: 'text-blue-500', bg: 'bg-blue-50' };
    if (code <= 77) return { icon: CloudSnow, color: 'text-sky-400', bg: 'bg-sky-50' };
    if (code <= 82) return { icon: CloudRain, color: 'text-blue-600', bg: 'bg-blue-100' };
    return { icon: CloudRain, color: 'text-indigo-500', bg: 'bg-indigo-50' };
  };

  const getWeatherLabel = (code) => {
    if (code === 0) return 'Ciel dégagé';
    if (code <= 3) return 'Nuageux';
    if (code <= 48) return 'Brouillard';
    if (code <= 57) return 'Bruine';
    if (code <= 67) return 'Pluie';
    if (code <= 77) return 'Neige';
    if (code <= 82) return 'Averses';
    if (code <= 86) return 'Averses neige';
    return 'Orage';
  };

  const formatDay = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    return { day: days[d.getDay()], date: d.getDate() + '/' + (d.getMonth() + 1) };
  };

  const scrollToSection = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedEventId) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/events/${selectedEventId}/documents-officiels`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      fetchEventInfos(selectedEventId);
    } catch (err) { console.error('Upload error:', err); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDocDelete = async (docId) => {
    if (!selectedEventId) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/events/${selectedEventId}/documents-officiels/${docId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchEventInfos(selectedEventId);
    } catch (err) { console.error('Delete error:', err); }
  };

  const hiddenSections = eventData?.hidden_sections || [];
  const sections = allSections.filter(s => !hiddenSections.includes(s.id));
  const selectedEvent = events.find(e => e.id === selectedEventId);

  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="infos-pratiques-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-lime-50 via-white to-fuchsia-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-fuchsia-200 rounded-full blur-3xl opacity-20" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">Guide des Événements</span>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-zinc-900 mb-6">{t('ip.title')}</h1>
            <p className="text-zinc-600 max-w-3xl mx-auto text-lg leading-relaxed">
              Bienvenue dans votre espace dédié pour préparer votre venue au Domaine de la Briouse. 
              Retrouvez ici toutes les informations essentielles : parcours, accès, horaires, restauration, résultats et contacts utiles.
            </p>
            <div className="mt-6">
              <ShareButton eventName={selectedEvent?.title || ''} variant="header" />
            </div>
          </div>

          {/* Sélection d'événement */}
          <h3 className="text-center text-sm font-semibold text-zinc-500 uppercase tracking-widest mb-4">{t('ip.selectEvent')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl mx-auto mb-8" data-testid="event-selector">
            {events.map((ev) => {
              const isSelected = selectedEventId === ev.id;
              const evDate = ev.start_date ? new Date(ev.start_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : null;
              return (
                <button
                  key={ev.id}
                  onClick={() => setSelectedEventId(ev.id)}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                    isSelected
                      ? 'border-fuchsia-500 bg-fuchsia-50 shadow-md ring-1 ring-fuchsia-200'
                      : 'border-zinc-200 bg-white hover:border-fuchsia-300 hover:bg-fuchsia-50/30'
                  }`}
                  data-testid={`event-btn-${ev.id}`}
                >
                  <div className={`w-2 h-10 rounded-full flex-shrink-0 ${isSelected ? 'bg-fuchsia-500' : 'bg-zinc-200'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${isSelected ? 'text-fuchsia-700' : 'text-zinc-800'}`}>{ev.title}</p>
                    {evDate && <p className="text-xs text-zinc-400">{evDate}</p>}
                  </div>
                  {isSelected && <ChevronRight className="h-4 w-4 text-fuchsia-500 flex-shrink-0" />}
                </button>
              );
            })}
          </div>

          {selectedEvent && (
            <div className="flex justify-center">
              <Button
                onClick={() => scrollToSection('acces')}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all"
                data-testid="prepare-venue-btn"
              >
                <Navigation className="mr-2 h-5 w-5" />
                Je prépare ma venue — {selectedEvent.title}
              </Button>
            </div>
          )}
        </div>
      </section>

      {selectedEvent && (
        <>
        {/* Sticky Nav avec nom de l'événement */}
        <nav ref={navRef} className="sticky top-20 z-40 bg-white/95 backdrop-blur-sm border-b border-zinc-200 shadow-sm" data-testid="sections-nav">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 py-1.5">
              <span className="text-xs font-bold text-fuchsia-600 uppercase tracking-wider whitespace-nowrap hidden md:block">{selectedEvent.title}</span>
              <div className="h-4 w-px bg-zinc-300 hidden md:block" />
              <div className="flex overflow-x-auto gap-1 flex-1 scrollbar-hide">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollToSection(s.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                      activeSection === s.id
                        ? 'bg-fuchsia-600 text-white shadow-md'
                        : 'text-zinc-600 hover:bg-zinc-100'
                    }`}
                    data-testid={`nav-${s.id}`}
                  >
                    <s.icon className="h-3.5 w-3.5" />
                    {s.labelKey ? t(s.labelKey) : s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 space-y-20">

        {/* 1. Accès & Plan */}
        <section id="acces" ref={(el) => (sectionRefs.current['acces'] = el)} data-testid="section-acces">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-fuchsia-100 rounded-xl"><MapPin className="h-6 w-6 text-fuchsia-600" /></div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-zinc-900">Accès & Plan du Site</h2>
              <p className="text-zinc-500 text-sm">Accès Domaine de la Briouse Sanxay</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-bold text-zinc-900 mb-2 flex items-center gap-2"><MapPin className="h-4 w-4 text-fuchsia-500" />Adresse</h3>
                  <p className="text-zinc-700 font-medium">Domaine de la Briouse</p>
                  <p className="text-zinc-600">La Briouse, 86600 Sanxay, France</p>
                </div>
                <div>
                  <h3 className="font-bold text-zinc-900 mb-2 flex items-center gap-2"><Navigation className="h-4 w-4 text-fuchsia-500" />Coordonnées GPS</h3>
                  <p className="text-zinc-600 font-mono text-sm">46.4875° N, -0.0395° E</p>
                </div>
                <div>
                  <h3 className="font-bold text-zinc-900 mb-2">Consignes d'arrivée</h3>
                  <ul className="space-y-2 text-sm text-zinc-600">
                    {(eventData?.acces?.consignes || [
                      "Camions & vans : suivre le fléchage dès l'entrée du village",
                      "Parking visiteurs gratuit sur site",
                      "Merci de respecter les emplacements indiqués par les bénévoles"
                    ]).map((c, i) => (
                      <li key={i} className="flex items-start gap-2"><ChevronRight className="h-4 w-4 text-lime-500 flex-shrink-0 mt-0.5" />{c}</li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-wrap gap-3 pt-2">
                  <a href="https://www.google.com/maps/dir//Domaine+de+la+Briouse,+La+Briouse,+86600+Sanxay" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-full" data-testid="itinerary-btn"><Navigation className="mr-2 h-4 w-4" />Voir l'itinéraire</Button>
                  </a>
                  {eventData?.acces?.plan_pdf && (
                    <a href={eventData.acces.plan_pdf} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="rounded-full"><Download className="mr-2 h-4 w-4" />Télécharger le plan</Button>
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="aspect-video">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2762.5!2d-0.0395!3d46.4875!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48047487e5555555%3A0x1234567890abcdef!2sLa%20Briouse%2C%2086600%20Sanxay!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Domaine de la Briouse" />
              </div>
            </Card>
          </div>
        </section>

        {/* 2. Parcours */}
        {!hiddenSections.includes('parcours') && (
        <section id="parcours" ref={(el) => (sectionRefs.current['parcours'] = el)} data-testid="section-parcours">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-lime-100 rounded-xl"><Route className="h-6 w-6 text-lime-600" /></div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-zinc-900">Parcours</h2>
              <p className="text-zinc-500 text-sm">Distances, dénivelés et cartes</p>
            </div>
          </div>
          {(eventData?.parcours?.length > 0) ? (
            <div className="space-y-8">
              {eventData.parcours.map((p, i) => (
                <div key={i} data-testid={`parcours-${i}`}>
                  {/* Map + Stats layout */}
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Map - takes 3/5 of width */}
                    {p.gpx_url && (
                      <Card className="border-0 shadow-lg overflow-hidden lg:col-span-3" data-testid={`parcours-map-${i}`}>
                        <GpxMapViewer gpxUrl={p.gpx_url} color={p.color} />
                      </Card>
                    )}
                    {/* Stats + Download - takes 2/5 */}
                    <div className={`space-y-4 ${p.gpx_url ? 'lg:col-span-2' : 'lg:col-span-5'}`}>
                      <Card className="border-0 shadow-lg">
                        <CardHeader className="text-white rounded-t-lg p-5" style={{ background: p.color || 'linear-gradient(to right, #84cc16, #65a30d)' }}>
                          <CardTitle className="flex items-center justify-between">
                            <span className="text-xl">{p.name || 'Parcours'}</span>
                          </CardTitle>
                          <CardDescription className="text-white/80">{p.distance}</CardDescription>
                        </CardHeader>
                        <CardContent className="p-5">
                          <div className="grid grid-cols-2 gap-4">
                            {p.distance && (
                              <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                                <Ruler className="h-5 w-5 text-fuchsia-500" />
                                <div>
                                  <p className="text-xs text-zinc-400 uppercase tracking-wider">Distance</p>
                                  <p className="font-bold text-zinc-900">{p.distance}</p>
                                </div>
                              </div>
                            )}
                            {p.denivele && (
                              <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                                <ArrowUpDown className="h-5 w-5 text-lime-500" />
                                <div>
                                  <p className="text-xs text-zinc-400 uppercase tracking-wider">Dénivelé</p>
                                  <p className="font-bold text-zinc-900">{p.denivele}</p>
                                </div>
                              </div>
                            )}
                            {p.altitude_min && (
                              <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                                <Mountain className="h-5 w-5 text-blue-500" />
                                <div>
                                  <p className="text-xs text-zinc-400 uppercase tracking-wider">Alt. min</p>
                                  <p className="font-bold text-zinc-900">{p.altitude_min}</p>
                                </div>
                              </div>
                            )}
                            {p.altitude_max && (
                              <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                                <Mountain className="h-5 w-5 text-red-500" />
                                <div>
                                  <p className="text-xs text-zinc-400 uppercase tracking-wider">Alt. max</p>
                                  <p className="font-bold text-zinc-900">{p.altitude_max}</p>
                                </div>
                              </div>
                            )}
                          </div>
                          {p.terrain && (
                            <div className="flex items-center gap-2 text-sm text-zinc-600 mt-4">
                              <TreePine className="h-4 w-4 text-lime-500" />Terrain : {p.terrain}
                            </div>
                          )}
                          {p.points_eau && (
                            <div className="flex items-center gap-2 text-sm text-zinc-600 mt-2">
                              <Droplets className="h-4 w-4 text-blue-500" />{p.points_eau}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      {/* Download buttons */}
                      <div className="flex flex-wrap gap-3">
                        {p.gpx_url && (
                          <a href={p.gpx_url} download className="flex-1">
                            <Button className="w-full text-white rounded-full" style={{ backgroundColor: p.color || '#65a30d' }} data-testid={`download-gpx-${i}`}>
                              <Download className="mr-2 h-4 w-4" />{t('ip.downloadGpx')}
                            </Button>
                          </a>
                        )}
                        {p.pdf_url && (
                          <a href={p.pdf_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                            <Button variant="outline" className="w-full rounded-full">
                              <FileDown className="mr-2 h-4 w-4" />PDF
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Elevation Profile */}
                  {p.gpx_url && (
                    <div className="mt-4" data-testid={`elevation-section-${i}`}>
                      <ElevationProfile gpxFilename={p.gpx_url} color={p.color || '#65a30d'} name={p.name} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Card className="border-2 border-dashed border-zinc-200 bg-zinc-50">
              <CardContent className="p-12 text-center">
                <Route className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-zinc-500 mb-2">Parcours à venir</h3>
                <p className="text-zinc-400">Les cartes des parcours seront publiées avant l'événement.</p>
              </CardContent>
            </Card>
          )}
        </section>
        )}

        {/* 3. Programme & Horaires */}
        <section id="programme" ref={(el) => (sectionRefs.current['programme'] = el)} data-testid="section-programme">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-indigo-100 rounded-xl"><Clock className="h-6 w-6 text-indigo-600" /></div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-zinc-900">{t('ip.programmeTitle')}</h2>
              <p className="text-zinc-500 text-sm">Planning détaillé de l'événement</p>
            </div>
          </div>
          {(eventData?.programme?.length > 0) ? (
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardContent className="p-0">
                <div className="divide-y divide-zinc-100">
                  {eventData.programme.map((item, i) => (
                    <div key={i} className={`flex items-start gap-4 p-5 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}`} data-testid={`programme-${i}`}>
                      <div className="text-center min-w-[70px]">
                        <span className="text-xl font-bold text-fuchsia-600">{item.heure}</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-zinc-900">{item.titre}</h4>
                        {item.description && <p className="text-sm text-zinc-500 mt-1">{item.description}</p>}
                        {item.lieu && <Badge variant="outline" className="mt-2 text-xs">{item.lieu}</Badge>}
                      </div>
                      {item.icon && <Timer className="h-5 w-5 text-zinc-300 flex-shrink-0" />}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : selectedEvent?.title?.toLowerCase().includes('endurance') ? (
            <ProgrammeEndurance />
          ) : (
            <Card className="border-2 border-dashed border-zinc-200 bg-zinc-50">
              <CardContent className="p-12 text-center">
                <Clock className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-zinc-500 mb-2">{t('ip.programmeComingSoon')}</h3>
                <p className="text-zinc-400">{t('ip.programmeComingSoonDesc')}</p>
              </CardContent>
            </Card>
          )}
          {eventData?.programme_pdf && (
            <div className="mt-4 text-center">
              <a href={eventData.programme_pdf} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-full"><Download className="mr-2 h-4 w-4" />{t('ip.downloadPdf')}</Button>
              </a>
            </div>
          )}
        </section>

        {/* 4. Documents & Roadbook */}
        <section id="documents" ref={(el) => (sectionRefs.current['documents'] = el)} data-testid="section-documents">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-amber-100 rounded-xl"><FileText className="h-6 w-6 text-amber-600" /></div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-zinc-900">{t('ip.documentsTitle')}</h2>
              <p className="text-zinc-500 text-sm">Tous les documents essentiels</p>
            </div>
          </div>

          {/* Article (e.g. Yonnel Estival) */}
          {eventData?.article && (
            <div className="mb-8" data-testid="article-section">
              <Card className="border-0 shadow-xl overflow-hidden">
                {/* Image gallery */}
                {eventData.article.images?.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-1">
                    {eventData.article.images.map((img, i) => (
                      <div key={i} className={`aspect-[4/3] overflow-hidden ${i === 0 ? 'col-span-2 row-span-2 md:col-span-1 md:row-span-1' : ''}`}>
                        <img src={img} alt={`${eventData.article.title} - ${i+1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />
                      </div>
                    ))}
                  </div>
                )}
                <CardContent className="p-6 md:p-8">
                  <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-zinc-900 mb-1">{eventData.article.title}</h3>
                  {eventData.article.subtitle && <p className="text-fuchsia-600 font-medium mb-4">{eventData.article.subtitle}</p>}
                  <p className="text-zinc-600 leading-relaxed mb-6">{eventData.article.intro}</p>

                  {/* Key facts */}
                  {eventData.article.facts?.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                      {eventData.article.facts.map((f, i) => (
                        <div key={i} className="flex items-start gap-2 p-3 bg-zinc-50 rounded-lg">
                          <Star className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-xs text-zinc-400 uppercase tracking-wider">{f.label}</span>
                            <p className="text-sm font-medium text-zinc-800">{f.value}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Article sections */}
                  {eventData.article.sections?.map((sec, i) => (
                    <div key={i} className="mb-4">
                      <h4 className="font-bold text-zinc-900 mb-1">{sec.title}</h4>
                      <p className="text-sm text-zinc-600 leading-relaxed">{sec.text}</p>
                    </div>
                  ))}

                  {/* Extra images */}
                  {eventData.article.extra_images?.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-6 rounded-xl overflow-hidden">
                      {eventData.article.extra_images.map((img, i) => (
                        <div key={i} className="aspect-[4/3] overflow-hidden">
                          <img src={img} alt={`Illustration ${i+1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Links */}
                  {eventData.article.links?.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-zinc-100">
                      {eventData.article.links.map((l, i) => (
                        <a key={i} href={l.url} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="rounded-full text-xs" data-testid={`article-link-${i}`}>
                            <ExternalLink className="mr-1.5 h-3 w-3" />{l.label}
                          </Button>
                        </a>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {(eventData?.documents?.length > 0) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {eventData.documents.map((doc, i) => {
                const iconMap = { roadbook: FileText, reglement: ShieldCheck, technique: FileDown };
                const DocIcon = iconMap[doc.type] || FileText;
                return (
                  <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer" className="block" data-testid={`doc-${i}`}>
                    <Card className="border-0 shadow-lg hover:-translate-y-1 hover:shadow-xl transition-all cursor-pointer">
                      <CardContent className="p-5 flex items-center gap-4">
                        <div className="p-3 bg-amber-50 rounded-xl"><DocIcon className="h-6 w-6 text-amber-600" /></div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-zinc-900">{doc.titre}</h4>
                          {doc.description && <p className="text-xs text-zinc-500">{doc.description}</p>}
                        </div>
                        <Download className="h-5 w-5 text-zinc-400" />
                      </CardContent>
                    </Card>
                  </a>
                );
              })}
            </div>
          ) : !eventData?.article && (
            <Card className="border-2 border-dashed border-zinc-200 bg-zinc-50">
              <CardContent className="p-12 text-center">
                <FileText className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-zinc-500 mb-2">Documents à venir</h3>
                <p className="text-zinc-400">Les documents seront mis en ligne avant l'événement.</p>
              </CardContent>
            </Card>
          )}

          {/* Guide Pratique Endurance - uniquement pour l'événement endurance */}
          {selectedEvent?.title?.toLowerCase().includes('endurance') && (
            <div className="mt-10" data-testid="guide-endurance-section">
              <GuideEndurance />
            </div>
          )}

          {/* Documents Officiels */}
          {(eventData?.documents_officiels?.length > 0 || isStaff) && (
            <div className="mt-8" data-testid="documents-officiels-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-zinc-900 flex items-center gap-2">
                  <FolderOpen className="h-5 w-5 text-amber-600" />{t('ip.documentsOfficiels')}
                </h3>
                {isStaff && (
                  <div>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleDocUpload} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" data-testid="doc-upload-input" />
                    <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline" className="rounded-full text-sm" data-testid="doc-upload-btn">
                      <Upload className="mr-2 h-4 w-4" />{uploading ? t('ip.uploading') : t('ip.uploadDoc')}
                    </Button>
                  </div>
                )}
              </div>
              {eventData?.documents_officiels?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {eventData.documents_officiels.map((doc, i) => (
                    <Card key={doc.id || i} className="border-0 shadow-lg" data-testid={`doc-officiel-${i}`}>
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="p-2.5 bg-red-50 rounded-xl flex-shrink-0"><ShieldCheck className="h-5 w-5 text-red-600" /></div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-zinc-900 text-sm truncate">{doc.titre}</h4>
                          {doc.size && <p className="text-xs text-zinc-400">{(doc.size / 1024).toFixed(0)} Ko</p>}
                        </div>
                        <a href={doc.url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost" className="text-fuchsia-600 hover:text-fuchsia-700" data-testid={`download-doc-officiel-${i}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </a>
                        {isStaff && (
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDocDelete(doc.id)} data-testid={`delete-doc-officiel-${i}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-2 border-dashed border-zinc-200 bg-zinc-50">
                  <CardContent className="p-8 text-center">
                    <FolderOpen className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
                    <p className="text-zinc-400 text-sm">{t('ip.noDocsYet')}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </section>

        {/* 5. Contacts */}
        <section id="contacts" ref={(el) => (sectionRefs.current['contacts'] = el)} data-testid="section-contacts">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-blue-100 rounded-xl"><Phone className="h-6 w-6 text-blue-600" /></div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-zinc-900">{t('ip.contactsTitle')}</h2>
              <p className="text-zinc-500 text-sm">Organisation, urgences, assistance</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(eventData?.contacts?.length > 0 ? eventData.contacts : [
              { role: "Organisation Générale", nom: "Brious'O Folies", tel: "07 49 51 30 90", email: "contact@briouso-folies.fr", urgence: false },
              { role: "Urgences Vétérinaires", nom: "", tel: "", email: "", urgence: true },
              { role: "Responsable Parcours", nom: "", tel: "", email: "", urgence: false },
              { role: "Assistance Technique", nom: "", tel: "", email: "", urgence: false }
            ]).filter(c => !(eventData?.hidden_contact_roles || []).includes(c.role)).map((contact, i) => (
              <Card key={i} className={`border-0 shadow-lg ${contact.urgence ? 'ring-2 ring-red-400 bg-red-50' : ''}`} data-testid={`contact-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-bold text-zinc-900">{contact.role}</h4>
                      {contact.nom && <p className="text-sm text-zinc-600">{contact.nom}</p>}
                    </div>
                    {contact.urgence && <Badge className="bg-red-500 text-white">{t('ip.emergency')}</Badge>}
                  </div>
                  <div className="space-y-2">
                    {contact.tel && (
                      <a href={`tel:${contact.tel.replace(/\s/g, '')}`} className="flex items-center gap-2 text-fuchsia-600 hover:text-fuchsia-700 font-medium">
                        <PhoneCall className="h-4 w-4" />{contact.tel}
                      </a>
                    )}
                    {contact.email && (
                      <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-zinc-600 hover:text-fuchsia-600">
                        <ExternalLink className="h-3 w-3" />{contact.email}
                      </a>
                    )}
                    {contact.link && (
                      <a href={contact.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
                        <ExternalLink className="h-4 w-4" />Voir la fiche
                      </a>
                    )}
                    {contact.contacts_list?.length > 0 && (
                      <div className="space-y-2 mt-1">
                        {contact.contacts_list.map((c, j) => (
                          <a key={j} href={`tel:${c.tel}`} className="flex items-center justify-between gap-2 p-2 bg-white rounded-lg hover:bg-red-100 transition-colors">
                            <span className="text-sm font-medium text-zinc-700">{c.label}</span>
                            <span className="text-sm font-bold text-red-600">{c.tel}</span>
                          </a>
                        ))}
                      </div>
                    )}
                    {!contact.tel && !contact.nom && !contact.link && !contact.contacts_list?.length && <p className="text-sm text-zinc-400 italic">Sera communiqué avant l'événement</p>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* 6. Restauration */}
        <section id="restauration" ref={(el) => (sectionRefs.current['restauration'] = el)} data-testid="section-restauration">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-orange-100 rounded-xl"><UtensilsCrossed className="h-6 w-6 text-orange-600" /></div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-zinc-900">Restauration & Menu</h2>
              <p className="text-zinc-500 text-sm">Repas, buvette et horaires de service</p>
            </div>
          </div>
          {eventData?.restauration?.menus?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {eventData.restauration.menus.map((menu, i) => (
                <Card key={i} className="border-0 shadow-lg" data-testid={`menu-${i}`}>
                  <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-lg">
                    <CardTitle>{menu.nom}</CardTitle>
                    <CardDescription className="text-white/80">{menu.horaires}</CardDescription>
                  </CardHeader>
                  <CardContent className="p-5">
                    <ul className="space-y-2">
                      {menu.items?.map((item, j) => (
                        <li key={j} className="flex justify-between items-center py-1 border-b border-zinc-100 last:border-0">
                          <span className="text-zinc-700">{item.nom}</span>
                          {item.prix && <span className="font-semibold text-fuchsia-600">{item.prix}</span>}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
              {eventData.restauration.reservation_url && (
                <div className="md:col-span-2 text-center">
                  <a href={eventData.restauration.reservation_url} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8"><Utensils className="mr-2 h-4 w-4" />Réserver un repas</Button>
                  </a>
                </div>
              )}
            </div>
          ) : (
            <Card className="border-2 border-dashed border-zinc-200 bg-zinc-50">
              <CardContent className="p-12 text-center">
                <UtensilsCrossed className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-zinc-500 mb-2">Menu à venir</h3>
                <p className="text-zinc-400">Les menus et la carte seront publiés avant l'événement.</p>
              </CardContent>
            </Card>
          )}
        </section>

        {/* 7. Résultats */}
        {!hiddenSections.includes('resultats') && (
        <section id="resultats" ref={(el) => (sectionRefs.current['resultats'] = el)} data-testid="section-resultats">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-yellow-100 rounded-xl"><Trophy className="h-6 w-6 text-yellow-600" /></div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-zinc-900">{t('ip.resultatsTitle')}</h2>
              <p className="text-zinc-500 text-sm">Résultats par catégorie et podiums</p>
            </div>
          </div>
          {eventData?.resultats?.categories?.length > 0 ? (
            <Tabs defaultValue={eventData.resultats.categories[0]?.nom} className="w-full">
              <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto mb-6">
                {eventData.resultats.categories.map((cat) => (
                  <TabsTrigger key={cat.nom} value={cat.nom} className="data-[state=active]:bg-yellow-500 data-[state=active]:text-white bg-zinc-100 rounded-full">{cat.nom}</TabsTrigger>
                ))}
              </TabsList>
              {eventData.resultats.categories.map((cat) => (
                <TabsContent key={cat.nom} value={cat.nom}>
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-0">
                      <table className="w-full">
                        <thead className="bg-zinc-50"><tr><th className="px-4 py-3 text-left text-sm font-semibold text-zinc-500">#</th><th className="px-4 py-3 text-left text-sm font-semibold text-zinc-500">Cavalier</th><th className="px-4 py-3 text-left text-sm font-semibold text-zinc-500">Cheval</th><th className="px-4 py-3 text-left text-sm font-semibold text-zinc-500">Temps</th></tr></thead>
                        <tbody className="divide-y divide-zinc-100">
                          {cat.classement?.map((entry, i) => (
                            <tr key={i} className={i < 3 ? 'bg-yellow-50' : ''}>
                              <td className="px-4 py-3">{i < 3 ? <Star className={`h-5 w-5 ${i === 0 ? 'text-yellow-500' : i === 1 ? 'text-zinc-400' : 'text-orange-400'}`} /> : <span className="text-zinc-500">{i + 1}</span>}</td>
                              <td className="px-4 py-3 font-medium text-zinc-900">{entry.cavalier}</td>
                              <td className="px-4 py-3 text-zinc-600">{entry.cheval}</td>
                              <td className="px-4 py-3 text-zinc-600 font-mono text-sm">{entry.temps}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            <Card className="border-2 border-dashed border-zinc-200 bg-zinc-50">
              <CardContent className="p-12 text-center">
                <Trophy className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-zinc-500 mb-2">Résultats à venir</h3>
                <p className="text-zinc-400">Les résultats seront publiés après les courses.</p>
              </CardContent>
            </Card>
          )}
          {eventData?.resultats?.pdf_url && (
            <div className="mt-4 text-center">
              <a href={eventData.resultats.pdf_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-full"><Download className="mr-2 h-4 w-4" />Voir les résultats complets</Button>
              </a>
            </div>
          )}
        </section>
        )}

        {/* 8. Sécurité */}
        <section id="securite" ref={(el) => (sectionRefs.current['securite'] = el)} data-testid="section-securite">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-red-100 rounded-xl"><ShieldCheck className="h-6 w-6 text-red-600" /></div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-zinc-900">{t('ip.securiteTitle')}</h2>
              <p className="text-zinc-500 text-sm">Consignes essentielles pour tous</p>
            </div>
          </div>
          {eventData?.securite_details?.length > 0 ? (
            <div className="grid grid-cols-1 gap-6">
              {eventData.securite_details.map((sec, i) => {
                const iconMap = { cavalier: ShieldCheck, cheval: Stethoscope, environnement: TreePine };
                const colorMap = { cavalier: 'fuchsia', cheval: 'lime', environnement: 'blue' };
                const bgMap = { cavalier: 'bg-fuchsia-50', cheval: 'bg-lime-50', environnement: 'bg-blue-50' };
                const SectionIcon = iconMap[sec.cat] || ShieldCheck;
                const color = colorMap[sec.cat] || 'fuchsia';
                return (
                  <Card key={i} className="border-0 shadow-lg overflow-hidden" data-testid={`securite-${i}`}>
                    <CardHeader className={bgMap[sec.cat] || 'bg-zinc-50'}>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <SectionIcon className={`h-5 w-5 text-${color}-500`} />{sec.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-5">
                      <ul className="space-y-2 mb-4">
                        {(sec.summary || []).map((item, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-zinc-600">
                            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />{item}
                          </li>
                        ))}
                      </ul>
                      {sec.details && (
                        <Accordion type="single" collapsible>
                          <AccordionItem value={`rule-${i}`} className="border rounded-lg px-4">
                            <AccordionTrigger className="text-sm font-semibold text-fuchsia-600 hover:no-underline" data-testid={`securite-details-btn-${i}`}>
                              {t('ip.readRules')}
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="prose prose-sm max-w-none text-zinc-600 whitespace-pre-line leading-relaxed" data-testid={`securite-details-content-${i}`}>
                                {sec.details}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: t('ip.secCavaliers'), icon: ShieldCheck, color: "fuchsia", items: eventData?.securite?.filter(s => s.cat === 'cavalier')?.map(s => s.text) || ["Port du casque obligatoire", "Respect des consignes de course", "Suivi du balisage obligatoire"] },
              { title: t('ip.secChevaux'), icon: Stethoscope, color: "lime", items: eventData?.securite?.filter(s => s.cat === 'cheval')?.map(s => s.text) || ["Contrôle vétérinaire obligatoire", "Eau disponible sur tout le parcours", "Tout signe de fatigue = arrêt immédiat"] },
              { title: t('ip.secEnvironnement'), icon: TreePine, color: "blue", items: eventData?.securite?.filter(s => s.cat === 'environnement')?.map(s => s.text) || ["Respectez les chemins balisés", "Ramassez vos déchets", "Protégez la faune et la flore locales"] }
            ].map((section, i) => (
              <Card key={i} className="border-0 shadow-lg" data-testid={`securite-${i}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <section.icon className={`h-5 w-5 text-${section.color}-500`} />{section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {section.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-zinc-600">
                        <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />{item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </section>

        {/* 9. Conseils */}
        <section id="conseils" ref={(el) => (sectionRefs.current['conseils'] = el)} data-testid="section-conseils">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-emerald-100 rounded-xl"><TreePine className="h-6 w-6 text-emerald-600" /></div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-zinc-900">{t('ip.conseilsTitle')}</h2>
              <p className="text-zinc-500 text-sm">{t('ip.conseilsSub')}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {eventData?.conseils?.items ? (
              <>
                <Card className="border-0 shadow-lg md:col-span-2">
                  <CardHeader><CardTitle className="flex items-center gap-2"><CheckSquare className="h-5 w-5 text-emerald-500" />Conseils & Recommandations</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {eventData.conseils.items.map((item, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-zinc-50 rounded-lg">
                          <CheckSquare className="h-4 w-4 text-lime-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-zinc-600">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
            <Card className="border-0 shadow-lg">
              <CardHeader><CardTitle className="flex items-center gap-2"><CheckSquare className="h-5 w-5 text-emerald-500" />{t('ip.checklistTitle')}</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {(eventData?.conseils?.checklist || [
                    "Licence FFE ou FFTE à jour", "Carnet de santé du cheval", "Certificat vétérinaire récent",
                    "Trousse de premiers soins", "Eau et nourriture pour le cheval", "Équipement de protection (casque, gilet)",
                    "Lampe frontale (si départ matinal)", "Numéro d'urgence enregistré"
                  ]).map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-600">
                      <CheckSquare className="h-4 w-4 text-lime-500 flex-shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardHeader><CardTitle className="flex items-center gap-2"><Thermometer className="h-5 w-5 text-blue-500" />{t('ip.logistiqueTitle')}</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {(eventData?.conseils?.logistique || [
                    { titre: "Météo", desc: "Consultez les prévisions ci-dessous. Prévoyez vêtements de pluie et protection solaire." },
                    { titre: "Équipement", desc: "Bottes ou chaussures fermées obligatoires. Prévoir couverture pour le cheval." },
                    { titre: "Arrivée", desc: "Arrivez 1h avant votre créneau pour l'installation et le contrôle vétérinaire." },
                    { titre: "Hébergement", desc: "Pensez à réserver tôt ! Boxes et paddocks disponibles sur place." }
                  ]).map((item, i) => (
                    <li key={i} className="text-sm">
                      <span className="font-medium text-zinc-900">{item.titre}</span>
                      <p className="text-zinc-500 mt-0.5">{item.desc}</p>
                    </li>
                  ))}
                </ul>
                <Link to="/venir" className="mt-4 inline-block">
                  <Button variant="outline" className="rounded-full text-sm"><ExternalLink className="mr-1 h-3 w-3" />{t('ip.viewAccommodation')}</Button>
                </Link>
              </CardContent>
            </Card>
              </>
            )}
          </div>

          {/* Météo 15 jours - Widget embarqué */}
          <Card className="border-0 shadow-xl mt-8" data-testid="weather-card">
            <CardHeader className="bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Sun className="h-5 w-5" />
                {t('ip.weatherTitle')}
              </CardTitle>
              <CardDescription className="text-white/80">Domaine de la Briouse, 86600 Sanxay</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {weather?.daily?.time ? (
                <div>
                  <div className="overflow-x-auto">
                    <div className="flex min-w-max">
                      {weather.daily.time.map((date, i) => {
                        const code = weather.daily.weather_code?.[i] ?? weather.daily.weathercode?.[i];
                        const w = getWeatherIcon(code);
                        const WeatherIcon = w.icon;
                        const { day, date: dateNum } = formatDay(date);
                        const isToday = i === 0;
                        return (
                          <div
                            key={date}
                            className={`flex flex-col items-center py-5 px-3 min-w-[85px] border-r border-zinc-100 last:border-r-0 transition-colors hover:bg-zinc-50 ${isToday ? 'bg-sky-50' : ''}`}
                            data-testid={`weather-day-${i}`}
                          >
                            <span className={`text-xs font-bold uppercase ${isToday ? 'text-sky-600' : 'text-zinc-500'}`}>{isToday ? "Auj." : day}</span>
                            <span className="text-xs text-zinc-400 mb-2">{dateNum}</span>
                            <div className={`p-2 rounded-full ${w.bg} mb-2`}>
                              <WeatherIcon className={`h-6 w-6 ${w.color}`} />
                            </div>
                            <span className="text-xs text-zinc-400 mb-1">{getWeatherLabel(code)}</span>
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-bold text-zinc-900">{Math.round(weather.daily.temperature_2m_max[i])}°</span>
                              <span className="text-xs text-zinc-400">{Math.round(weather.daily.temperature_2m_min[i])}°</span>
                            </div>
                            {weather.daily.precipitation_sum[i] > 0 && (
                              <div className="flex items-center gap-0.5 mt-1">
                                <Droplets className="h-3 w-3 text-blue-400" />
                                <span className="text-xs text-blue-500 font-medium">{weather.daily.precipitation_sum[i]}mm</span>
                              </div>
                            )}
                            {(weather.daily.wind_speed_10m_max?.[i] ?? 0) > 30 && (
                              <div className="flex items-center gap-0.5 mt-0.5">
                                <Wind className="h-3 w-3 text-zinc-400" />
                                <span className="text-xs text-zinc-400">{Math.round(weather.daily.wind_speed_10m_max[i])}km/h</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {weather.daily.time.length < 7 && (
                    <div className="px-4 py-3 bg-sky-50 border-t border-sky-100 text-center">
                      <a href="https://www.meteofrance.com/previsions-meteo-france/sanxay/86600" target="_blank" rel="noopener noreferrer" className="text-sm text-sky-600 hover:text-sky-700 font-medium">
                        {t('ip.weatherForecast')} →
                      </a>
                    </div>
                  )}
                </div>
              ) : weatherLoading ? (
                <div className="p-8 text-center text-zinc-400">{t('ip.weatherLoading')}</div>
              ) : (
                <div className="p-6 text-center">
                  <p className="text-zinc-500 text-sm">{t('ip.weatherUnavailable')}</p>
                  <a href="https://www.meteofrance.com/previsions-meteo-france/sanxay/86600" target="_blank" rel="noopener noreferrer" className="text-sm text-fuchsia-500 hover:underline mt-1 inline-block">
                    {t('ip.weatherLink')}
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </div>

      {/* SEO Footer */}
      <section className="py-12 bg-zinc-50 border-t" data-testid="seo-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center text-sm text-zinc-400 space-y-2">
            {eventData?.seo_keywords?.length > 0 ? (
              <>
                <p>{eventData.seo_keywords.slice(0, Math.ceil(eventData.seo_keywords.length / 3)).join(' \u00b7 ')}</p>
                <p>{eventData.seo_keywords.slice(Math.ceil(eventData.seo_keywords.length / 3), Math.ceil(2 * eventData.seo_keywords.length / 3)).join(' \u00b7 ')}</p>
                <p>{eventData.seo_keywords.slice(Math.ceil(2 * eventData.seo_keywords.length / 3)).join(' \u00b7 ')}</p>
              </>
            ) : (
              <>
                <p>Endurance équestre Sanxay &middot; Course endurance cheval Vienne &middot; Événement équestre Domaine de la Briouse</p>
                <p>Compétition endurance Nouvelle-Aquitaine &middot; Parcours endurance cheval &middot; Résultats endurance équestre</p>
              </>
            )}
          </div>
        </div>
      </section>
      </>
      )}

      {/* Floating share button */}
      <ShareButton eventName={selectedEvent?.title || ''} variant="floating" />

      {/* Scroll to top */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 p-3 bg-fuchsia-600 text-white rounded-full shadow-lg hover:bg-fuchsia-700 transition-all z-40"
        data-testid="scroll-top-btn"
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    </div>
  );
}
